<template>
  <div
    :class="`booking-search-controls booking-search-controls${
      full_width ? '--full-width' : '--inline'
    }`"
  >
    <!-- Occupancy Selector -->
    <b-col
      cols="12"
      :lg="full_width ? 2 : 8"
      class="booking-search-controls__occupancy"
    >
      <traveler-occupancy-input
        :travelers="travelers"
        :max_adults="max_adults"
        :max_children="max_children"
        @update:travelers="on_travelers_update"
        @update:adults="on_adults_update"
        @update:children="on_children_update"
        :occupancy_alignment="full_width ? 'end' : 'auto'"
      />
    </b-col>

    <!-- Search Button -->
    <b-col cols="12" :lg="full_width ? 1 : 4">
      <div class="booking-search-controls__search">
        <vb-button
          @click="handle_search_click"
          :vb_options="{
            text: loading ? 'Attendi...' : 'Cerca',
            disabled: loading || !can_search_now || disabled,
            variant: 'info',
            block: true,
          }"
        />
      </div>
    </b-col>
  </div>
</template>

<script>
import TravelerOccupancyInput from "./traveler-occupancy-input.vue";
import { is_origin_destination_valid } from "@liveflight/utilities/origin-destination";
import { get_config } from "@/utilities/";

export default {
  name: "booking-search-controls",
  components: {
    "traveler-occupancy-input": TravelerOccupancyInput,
  },
  props: {
    // External disable override (optional)
    disabled: {
      type: Boolean,
      default: false,
    },
    // Full width layout mode
    full_width: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // Travelers state from Vuex
    adults: {
      get() {
        return this.$store.state.liveflight.adults;
      },
      set(value) {
        this.$store.commit("liveflight/adults", value);
      },
    },
    children: {
      get() {
        return this.$store.state.liveflight.children;
      },
      set(value) {
        this.$store.commit("liveflight/children", value);
      },
    },
    travelers: {
      get() {
        return this.$store.state.liveflight.travelers;
      },
      set(value) {
        this.$store.commit("liveflight/travelers", value);
      },
    },
    // Configuration limits
    max_adults() {
      return get_config("guiSettings.searchMaxAdults", 6);
    },
    max_children() {
      return get_config("guiSettings.searchMaxChildren", 4);
    },
    // Loading state
    loading() {
      return this.$store.state.liveflight.loading;
    },
    // Origin-destinations for validation
    origin_destinations() {
      return this.$store.state.liveflight.origin_destinations;
    },
    return_flight() {
      return this.$store.state.liveflight.return_flight;
    },
    // Validate if search is possible
    can_search_now() {
      return (
        !this.loading &&
        this.origin_destinations.every(is_origin_destination_valid)
      );
    },
  },
  methods: {
    on_travelers_update(value) {
      this.travelers = value;
      this.$emit("occupancy-change", {
        travelers: value,
        adults: this.adults,
        children: this.children,
      });
    },
    on_adults_update(value) {
      this.adults = value;
      this.$emit("occupancy-change", {
        travelers: this.travelers,
        adults: value,
        children: this.children,
      });
    },
    on_children_update(value) {
      this.children = value;
      this.$emit("occupancy-change", {
        travelers: this.travelers,
        adults: this.adults,
        children: value,
      });
    },
    handle_search_click() {
      if (this.can_search_now && !this.disabled) {
        this.$emit("search");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.booking-search-controls {
  display: contents; // Allows children to participate in parent's flex/grid layout

  &__occupancy {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    margin-bottom: $grid-gutter-width / 2;

    @include media-breakpoint-up(lg) {
      margin-bottom: 0;
      justify-content: flex-end;
    }
  }

  &--inline {
    @include media-breakpoint-up(lg) {
      & > * {
        padding-left: 0 !important;
      }
    }
  }

  &--full-width {
    border-top: 1px solid $light-gray;
    margin-top: $grid-gutter-width / 2;
    padding-top: $grid-gutter-width / 2;

    width: 100%;

    @include media-breakpoint-up(lg) {
      display: flex;
      justify-content: space-between;
      & > *:not(:nth-child(1)) {
        padding-left: 0 !important;
      }
    }
  }

  &__search {
    height: 100%;
    display: flex;
    align-items: flex-end;

    ::v-deep .vb-btn {
      width: 100%;
    }
  }
}
</style>
